import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import tokens from '../../../../common/styles/design-tokens';

// Import your individual filter components
import FilterTextInput from './form/FilterTextInput';
import FilterComboBox from './form/FilterComboBox';
import FilterCheckbox from './form/FilterCheckbox';
import FilterRangeSlider from './form/FilterRangeSlider';
import FilterDatePicker from './form/FilterDatePicker';
import FilterNumberInput from './form/FilterNumberInput';
import FilterDiscoverySearchTypePills from './form/FilterDiscoverySearchTypePills';
import { SEARCH_TYPE_FILTER_IDS } from '../searchTypeFilterIds';

/**
 * FilterComponentBuilder
 * Automatically renders a list of filters based on the new JSON structure.
 * * Logic:
 * 1. Receives an array of filters (props.filterOptions).
 * 2. Filters them into 'Main' (advanced=false) and 'Advanced' (advanced=true).
 * 3. Renders Main items.
 * 4. Renders Toggle Button if Advanced items exist.
 * 5. Renders Advanced items when toggled.
 */
const isDiscoverySearchTypePill = (ftOption, groupKey) =>
    groupKey === 'DISCOVER' &&
    ftOption?.filterType === 'HAS_VALUE' &&
    SEARCH_TYPE_FILTER_IDS.has(ftOption.id);

const renderOrderedFilters = (items, groupKey, commonProps) => {
    const pillOptions = items.filter((f) => isDiscoverySearchTypePill(f, groupKey));
    const out = [];
    let pillsInserted = false;

    items.forEach((ftOption) => {
        if (isDiscoverySearchTypePill(ftOption, groupKey)) {
            if (!pillsInserted && pillOptions.length > 0) {
                out.push(
                    <FilterDiscoverySearchTypePills
                        key="discovery-search-type-pills"
                        options={pillOptions}
                        stateObj={commonProps.stateObj}
                        handleSubmitFilter={commonProps.handleSubmitFilter}
                        handleRemoveFilter={commonProps.handleRemoveFilter}
                    />
                );
                pillsInserted = true;
            }
        } else if (groupKey === 'DISCOVER' && ftOption.id === 'SEARCH_ACROSS') {
            // Placeholder row; "Search across:" label is on the pill group above the chips.
        } else {
            out.push(
                <FilterFactory
                    key={ftOption.id}
                    ftOption={ftOption}
                    {...commonProps}
                />
            );
        }
    });
    return out;
};

const FilterComponentBuilder = (props) => {
    const {
        filterOptions, // This corresponds to the "filters" array in your JSON
        groupKey,
        compact = false,
        stateObj,
        handleKeyValueChange,
        metaDataObj,
        handleSubmitFilter,
        handleRemoveFilter,
        productSubscription
    } = props;

    const [showAdvanced, setShowAdvanced] = useState(false);

    if (!filterOptions || !Array.isArray(filterOptions)) return null;

    const mainItems = filterOptions.filter(f => !f.advanced);
    const advancedItems = filterOptions.filter(f => f.advanced);

    const commonProps = {
        stateObj,
        handleKeyValueChange,
        metaDataObj,
        handleSubmitFilter,
        handleRemoveFilter,
        productSubscription
    };

    return (
        <FilterListContainer $compact={compact}>
            {/* 1. Render MAIN items */}
            {renderOrderedFilters(mainItems, groupKey, commonProps)}

            {advancedItems.length > 0 && (
                <>
                    <AdvancedToggle $compact={compact} onClick={() => setShowAdvanced(!showAdvanced)}>
                        {showAdvanced ? 'HIDE ADVANCED' : 'ADVANCED'}
                    </AdvancedToggle>

                    {showAdvanced && (
                        <AdvancedSection $compact={compact}>
                            {renderOrderedFilters(advancedItems, groupKey, commonProps)}
                        </AdvancedSection>
                    )}
                </>
            )}
        </FilterListContainer>
    );
};

const FilterFactory = (props) => {
    const { ftOption } = props;

    if (!ftOption) return null;

    switch (ftOption.filterType) {
        case 'DROP_DOWN':
            return <FilterComboBox {...props} />;

        case 'HAS_VALUE':
            return <FilterCheckbox {...props} />;

        case 'RANGE': // Assuming you might have ranges
            return <FilterRangeSlider {...props} />;

        case 'DATE':
            return <FilterDatePicker {...props} />;

        case 'NUMBER':
            return <FilterNumberInput {...props} />;

        case 'CONTAINS':
        default:
            return <FilterTextInput {...props} />;
    }
};

export default FilterComponentBuilder;

// --- Styled Components ---
const FilterListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(p) => (p.$compact ? tokens.spacing.xs : tokens.spacing.sm)};

    ${(p) =>
        p.$compact &&
        css`
            .formTextLabel,
            .form-label,
            label.form-label {
                text-transform: none !important;
                font-size: 11px !important;
                letter-spacing: 0.02em;
            }
        `}
`;

const AdvancedToggle = styled.div`
    font-size: ${(p) => (p.$compact ? '10px' : tokens.typography.fontSize.xs)};
    color: ${(p) => (p.$compact ? tokens.colors.primary : tokens.colors.cyan)};
    text-align: right;
    cursor: pointer;
    font-weight: ${tokens.typography.fontWeight.semibold};
    margin-top: ${tokens.spacing.xs};
    margin-bottom: ${tokens.spacing.xs};
    user-select: none;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
        text-decoration: underline;
    }
`;

const AdvancedSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(p) => (p.$compact ? tokens.spacing.xs : tokens.spacing.sm)};
    padding-top: ${tokens.spacing.xs};

    animation: fadeIn 0.3s ease-in-out;
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-5px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;